import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import { Share2, Download, Copy, X, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

const ShareCard = ({ 
  isOpen, 
  onClose, 
  title, 
  content, 
  date, 
  type = 'question' // 'question' or 'dream'
}) => {
  const cardRef = useRef(null);
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const getExcerpt = (text, maxLength = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'ro' ? 'ro-RO' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        logging: false,
      });
      
      const link = document.createElement('a');
      link.download = `universe-${type}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to generate image:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyText = async () => {
    const textToCopy = `${title}\n\n${content}\n\n— Universe | ${formatDate(date)}`;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        // Try to share with image first
        const canvas = await html2canvas(cardRef.current, {
          scale: 2,
          backgroundColor: null,
          useCORS: true,
          logging: false,
        });
        
        canvas.toBlob(async (blob) => {
          if (blob) {
            const file = new File([blob], `universe-${type}.png`, { type: 'image/png' });
            
            try {
              await navigator.share({
                title: 'Universe',
                text: `${title}\n\n${getExcerpt(content, 100)}`,
                files: [file]
              });
            } catch {
              // Fallback to text-only share
              await navigator.share({
                title: 'Universe',
                text: `${title}\n\n${content}\n\n— Universe`
              });
            }
          }
        }, 'image/png');
      } catch (error) {
        console.error('Share failed:', error);
      }
    } else {
      // Fallback for desktop - copy to clipboard
      handleCopyText();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10"
            data-testid="share-close-btn"
          >
            <X size={24} />
          </button>

          {/* Shareable Card */}
          <div
            ref={cardRef}
            className="relative overflow-hidden rounded-2xl"
            style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
            }}
          >
            {/* Decorative elements */}
            <div className="absolute inset-0 overflow-hidden">
              {/* Stars */}
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full opacity-40"
                  style={{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animation: `twinkle ${2 + Math.random() * 2}s ease-in-out infinite`,
                    animationDelay: `${Math.random() * 2}s`
                  }}
                />
              ))}
              {/* Gradient orbs */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-violet-500/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-amber-500/20 rounded-full blur-3xl" />
            </div>

            {/* Content */}
            <div className="relative p-8">
              {/* Header with logo */}
              <div className="flex items-center gap-3 mb-6">
                <div 
                  className="w-6 h-6 rounded-full"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #60a5fa, #3b82f6, #1d4ed8)',
                    boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(59, 130, 246, 0.3)'
                  }}
                />
                <div>
                  <h3 className="text-white font-bold tracking-wide">UNIVERSE</h3>
                  <p className="text-slate-400 text-xs">{t('cosmicWisdom')}</p>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-xl font-bold text-gradient-gold mb-4 font-heading">
                {type === 'dream' ? t('dreamInterpretation') : t('theUniverseSpeaks')}
              </h2>

              {/* Content excerpt */}
              <p className="text-slate-300 leading-relaxed mb-6 text-sm">
                "{getExcerpt(content, 250)}"
              </p>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <span className="text-slate-500 text-xs">{formatDate(date)}</span>
                <div className="flex items-center gap-2">
                  <span className="text-amber-400 text-xs">universe.app</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleShare}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-medium hover:from-violet-500 hover:to-purple-500 transition-all flex items-center justify-center gap-2"
              data-testid="share-btn"
            >
              <Share2 size={18} />
              <span>{t('share')}</span>
            </button>
            
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="py-3 px-4 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="download-btn"
            >
              {downloading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <Download size={18} />
              )}
            </button>
            
            <button
              onClick={handleCopyText}
              className="py-3 px-4 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-700 transition-all flex items-center justify-center gap-2"
              data-testid="copy-btn"
            >
              {copied ? (
                <Check size={18} className="text-emerald-400" />
              ) : (
                <Copy size={18} />
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ShareCard;
